import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

// Os dados dos links de serviço e indústria podem ser importados ou passados como props.
// Para simplificar, vamos defini-los aqui. Adapte conforme sua estrutura.
const services = {
  ai: {
    categoryKey: 'features.featureFive.categories.ai.category',
    links: [
      { to: "/servico/ia-generativa", textKey: 'features.featureFive.categories.ai.topics.generative.title', icon: 'fa-robot' },
      { to: "/servico/ia-personalizada", textKey: 'features.featureFive.categories.ai.topics.custom.title', icon: 'fa-cogs' },
    ]
  },
  professionals: {
    categoryKey: 'features.featureFive.categories.professionals.category',
    links: [
      { to: "/servico/recrutamento-tech", textKey: 'features.featureFive.categories.professionals.topics.recruitment.title', icon: 'fa-user-tie' },
      { to: "/servico/outsourcing", textKey: 'features.featureFive.categories.professionals.topics.outsourcing.title', icon: 'fa-handshake' },
    ]
  },
  software: {
    categoryKey: 'features.featureFive.categories.software.category',
    links: [
      { to: "/servico/sistemas-plataformas", textKey: 'features.featureFive.categories.software.topics.systems.title', icon: 'fa-window-maximize' },
      { to: "/servico/aplicativos", textKey: 'features.featureFive.categories.software.topics.apps.title', icon: 'fa-mobile-alt' },
    ]
  },
  infrastructure: {
    categoryKey: 'features.featureFive.categories.infrastructure.category',
    links: [
      { to: "/servico/migracao-cloud", textKey: 'features.featureFive.categories.infrastructure.topics.migration.title', icon: 'fa-cloud-upload-alt' },
      { to: "/servico/gerenciar-otimizar", textKey: 'features.featureFive.categories.infrastructure.topics.management.title', icon: 'fa-sliders-h' },
    ]
  }
};

const cases = [
  { to: "/cases/rightee", title: "Rightee", icon: "fa-golf-ball" },
  { to: "/cases/cardiohome", title: "Cardiohome", icon: "fa-heartbeat" },
  { to: "/cases/grupovisco", title: "Grupo Visco", icon: "fa-eye" },
];

const industries = [
    { to: "/industria/agronegocio", textKey: 'navbar.industries.agribusiness', icon: 'fa-leaf' },
    { to: "/industria/bets-igaming", textKey: 'navbar.industries.betting', icon: 'fa-dice' },
    { to: "/industria/educacao", textKey: 'navbar.industries.education', icon: 'fa-graduation-cap' },
    { to: "/industria/financeiro", textKey: 'navbar.industries.financial', icon: 'fa-chart-bar' },
    { to: "/industria/logistica", textKey: 'navbar.industries.logistics', icon: 'fa-truck' },
    { to: "/industria/startups", textKey: 'navbar.industries.startups', icon: 'fa-lightbulb' },
    { to: "/industria/streaming", textKey: 'navbar.industries.streaming', icon: 'fa-play-circle' },
    { to: "/industria/tecnologia", textKey: 'navbar.industries.technology', icon: 'fa-microchip' },
    { to: "/industria/varejo-e-moda", textKey: 'navbar.industries.retail', icon: 'fa-shopping-bag' },
];

/**
 * OffCanvasMenu - Menu mobile com navegação corrigida
 * 
 * Mudanças implementadas:
 * 1. Substituição de Links por botões para evitar conflitos de navegação
 * 2. Função handleNavigation que fecha o modal antes de navegar
 * 3. Suporte para animação de cima para baixo (slide-from-top)
 * 4. Fallback para casos onde Bootstrap não está disponível
 */
const OffCanvasMenu = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  // Garante que o scroll seja restaurado quando o modal for fechado
  useEffect(() => {
    const handleOffcanvasHidden = () => {
      restoreScroll();
    };

    const offcanvas = document.getElementById('offcanvasWithBackdrop');
    if (offcanvas) {
      offcanvas.addEventListener('hidden.bs.offcanvas', handleOffcanvasHidden);
    }

    return () => {
      if (offcanvas) {
        offcanvas.removeEventListener('hidden.bs.offcanvas', handleOffcanvasHidden);
      }
    };
  }, []);

  const handleLanguageChange = (lang) => {
    if (language !== lang) {
      toggleLanguage();
    }
  };

  // Função para restaurar o scroll do body
  const restoreScroll = () => {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    document.body.classList.remove('modal-open', 'offcanvas-open');
    
    // Remove qualquer estilo inline que possa estar bloqueando o scroll
    const style = document.body.getAttribute('style');
    if (style) {
      const newStyle = style
        .replace(/overflow:\s*[^;]+;?/g, '')
        .replace(/padding-right:\s*[^;]+;?/g, '')
        .trim();
      
      if (newStyle) {
        document.body.setAttribute('style', newStyle);
      } else {
        document.body.removeAttribute('style');
      }
    }
  };

  // Função para navegar e fechar o modal
  const handleNavigation = (path) => {
    // Fecha o modal primeiro
    const offcanvas = document.getElementById('offcanvasWithBackdrop');
    if (offcanvas) {
      // Tenta usar Bootstrap se disponível
      if (window.bootstrap && window.bootstrap.Offcanvas) {
        const bsOffcanvas = window.bootstrap.Offcanvas.getInstance(offcanvas);
        if (bsOffcanvas) {
          bsOffcanvas.hide();
        }
      } else {
        // Fallback: remove a classe show manualmente
        offcanvas.classList.remove('show');
        const backdrop = document.querySelector('.offcanvas-backdrop');
        if (backdrop) {
          backdrop.remove();
        }
        document.body.classList.remove('offcanvas-open');
      }
    }
    
    // Restaura o scroll do body imediatamente
    restoreScroll();
    
    // Navega após um pequeno delay para garantir que o modal feche
    setTimeout(() => {
      navigate(path);
      // Garante que o scroll seja restaurado após a navegação também
      setTimeout(() => {
        restoreScroll();
      }, 100);
    }, 150);
  };

  return (
    <div className="offcanvas-body">
      <ul className="navbar-nav_mobile navbar-nav">
        {/* Dropdown de Serviços */}
        <li className="nav-item">
            <button
                className="nav-link"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#services-collapse"
                aria-expanded="false"
                aria-controls="services-collapse"
            >
                {t('navbar.services')} <i className="far fa-chevron-down ms-1"></i>
            </button>
            <div className="collapse" id="services-collapse">
                <ul className="nav flex-column ps-3">
                    {Object.values(services).map(category => (
                        <React.Fragment key={category.categoryKey}>
                            <li className="nav-item_mobile-title">{t(category.categoryKey)}</li>
                            {category.links.map(link => (
                                <li className="nav-item" key={link.to}>
                                    <button 
                                        className="nav-link" 
                                        onClick={() => handleNavigation(link.to)}
                                        style={{ background: 'none', border: 'none', textAlign: 'left', width: '100%' }}
                                    >
                                        <i className={`far ${link.icon} me-2`}></i>{t(link.textKey)}
                                    </button>
                                </li>
                            ))}
                        </React.Fragment>
                    ))}
                </ul>
            </div>
        </li>

        {/* Dropdown de Indústrias */}
        <li className="nav-item">
            <button
                className="nav-link"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#industries-collapse"
                aria-expanded="false"
                aria-controls="industries-collapse"
            >
                {t('navbar.industry')} <i className="far fa-chevron-down ms-1"></i>
            </button>
            <div className="collapse" id="industries-collapse">
                <ul className="nav flex-column ps-3">
                    {industries.map(link => (
                         <li className="nav-item" key={link.to}>
                             <button 
                                 className="nav-link" 
                                 onClick={() => handleNavigation(link.to)}
                                 style={{ background: 'none', border: 'none', textAlign: 'left', width: '100%' }}
                             >
                                <i className={`far ${link.icon} me-2`}></i>{t(link.textKey)}
                            </button>
                         </li>
                    ))}
                </ul>
            </div>
        </li>

        {/* Dropdown de Cases */}
        <li className="nav-item">
            <button
                className="nav-link"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#cases-collapse"
                aria-expanded="false"
                aria-controls="cases-collapse"
            >
                {t('navbar.cases.cases')} <i className="far fa-chevron-down ms-1"></i>
            </button>
            <div className="collapse" id="cases-collapse">
                <ul className="nav flex-column ps-3">
                    {cases.map(caseItem => (
                        <li className="nav-item" key={caseItem.to}>
                            <button 
                                className="nav-link" 
                                onClick={() => handleNavigation(caseItem.to)}
                                style={{ background: 'none', border: 'none', textAlign: 'left', width: '100%' }}
                            >
                                <i className={`far ${caseItem.icon} me-2`}></i>{caseItem.title}
                            </button>
                        </li>
                    ))}
                    <li className="nav-item">
                        <button 
                            className="nav-link" 
                            onClick={() => handleNavigation('/cases')}
                            style={{ background: 'none', border: 'none', textAlign: 'left', width: '100%', fontWeight: 'bold' }}
                        >
                            <i className="far fa-arrow-right me-2"></i>Ver mais cases
                        </button>
                    </li>
                </ul>
            </div>
        </li>

        {/* Link direto para Carreiras */}
        <li className="nav-item">
          <button 
            className="nav-link" 
            onClick={() => handleNavigation('/careers')}
            style={{ background: 'none', border: 'none', textAlign: 'left', width: '100%' }}
          >
            {t('navbar.carrers')}
          </button>
        </li>
      </ul>

      {/* Ações e Idioma no final */}
      <div className="mt-4">
        <button 
          className="btn btn-primary w-100" 
          onClick={() => handleNavigation('/request-demo')}
        >
          {t('navbar.contact')}
        </button>
      </div>

      <div className="d-flex justify-content-center align-items-center mt-4">
          <button
            onClick={() => handleLanguageChange('pt')}
            className={`btn btn-link text-decoration-none p-1 ${language === 'pt' ? 'active' : ''}`}
            style={{ opacity: language === 'pt' ? 1 : 0.5 }}
            aria-label="Mudar para Português"
          >
            <img src="/assets/img/flags/brazil.svg" alt="Português" width="28" style={{ borderRadius: '4px' }} />
          </button>
          <button
            onClick={() => handleLanguageChange('en')}
            className={`btn btn-link text-decoration-none p-1 ${language === 'en' ? 'active' : ''}`}
            style={{ opacity: language === 'en' ? 1 : 0.5 }}
            aria-label="Switch to English"
          >
            <img src="/assets/img/flags/usa.svg" alt="English" width="28" style={{ borderRadius: '4px' }} />
          </button>
      </div>
    </div>
  );
};

export default OffCanvasMenu;